import {
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { ConsultantDocument } from '@lib/models/consultant/consultant-document.model';
import { OuiButton } from 'omnium-ui/button';
import { OuiLoading } from 'omnium-ui/loading';
import { OuiTableModule } from 'omnium-ui/table';
import { DatePipe } from '@angular/common';
import { OuiIconModule } from 'omnium-ui/icon';
import { StatusBackOfficeTagComponent } from '@lib/components/status-tag/status-tag.component';
import {
  DOCUMENT_CAN_BE_UPLOADED_BO_SECTION,
  DocumentCanBeUploadedBoType,
  DocumentConsultantType,
} from '@lib/models/documents/document-consultant-type';
import {
  DOCUMENT_SECTION_LABELS,
  REVERSED_DOCUMENT_SECTION_LABELS,
} from '@lib/models/documents/document-consultant-section';
import { MenuNode } from 'omnium-ui/shared';
import { OuiMenu } from 'omnium-ui/menu';
import { OuiDialogService } from 'omnium-ui/dialog';
import { UpdateDocSectionDialogComponent } from '../update-doc-section-dialog/update-doc-section-dialog.component';
import { take } from 'rxjs';
import { DocumentRenouvellementManuelDialogComponent } from '../document-renouvellement-manuel-dialog/document-renouvellement-manuel-dialog.component';

interface GroupedDocuments {
  sectionDocument: string;
  documents: ConsultantDocument[];
}

@Component({
  selector: 'lib-fiche-consultant-documents',
  standalone: true,
  imports: [
    OuiButton,
    OuiLoading,
    OuiTableModule,
    OuiIconModule,
    OuiMenu,
    DatePipe,
    StatusBackOfficeTagComponent,
  ],
  templateUrl: './fiche-consultant-documents.component.html',
  styleUrl: './fiche-consultant-documents.component.scss',
})
export class FicheConsultantDocumentsComponent {
  private readonly dialogService = inject(OuiDialogService);

  @HostBinding('class.dragover') isDragOver = false;

  @ViewChild('fileInput', { static: false })
  private fileInput?: ElementRef<HTMLInputElement>;

  readonly documents = input.required<ConsultantDocument[] | null>();
  readonly viewerDocument = input<ConsultantDocument | undefined>(undefined);

  readonly documentOpen = output<{
    document: ConsultantDocument;
    isUploadDoc: boolean;
  }>();
  readonly onRefreshDocs = output();
  readonly setDisplayDemandePjPanel = output();

  readonly isLoading = signal<boolean>(true);
  readonly fileUrls = signal<string[]>([]);
  readonly filenames = signal<string[]>([]);
  readonly selectedFile = signal<File | undefined>(undefined);
  private currentObjectUrl?: string;

  readonly groupedDocuments = computed(() => this.groupByTypeDocument(this.documents()!));

  readonly selectedSection = signal<string | undefined>(undefined);
  readonly selectedDocName = signal<string | undefined>(undefined);
  readonly selectedDocId = signal<string | undefined>(undefined);
  readonly selectedDocType = signal<string | undefined>(undefined);

  readonly enableRenManuelForDocs: DocumentConsultantType[] = [
    'notification_affiliation_agc',
    'notification_affiliation_pl',
    'notification_affiliation_soc',
    'cerfa_3558_agc',
    'cerfa_3558_pl',
    'cerfa_3558_soc',
  ];

  readonly actionMenu = computed<MenuNode[]>(() => [
    {
      icon: 'edit_document',
      name: 'Modifier la section',
      action: () =>
        this.openUpdateDocSectionDialog(
          this.selectedSection()!,
          this.selectedDocName()!,
          this.selectedDocId()!
        ),
    },
    {
      icon: 'upload_file',
      name: 'Demander un renouvellement',
      disabled: !this.enableRenManuelForDocs.includes(
        this.selectedDocType() as DocumentConsultantType
      ),
      action: () =>
        this.openDocRenouvellementManuelDialog(
          this.selectedDocName()!,
          this.selectedDocId()!
        ),
    },
  ]);

  readonly displayedColumns = [
    'nom',
    'type',
    'dateDepot',
    'dateExpiration',
    'statut',
    'actions',
  ];

  constructor() {
    effect(() => {
      if (!this.viewerDocument()) {
        this.clearSelectedFile();
      }
    });

    effect(() => {
      if (this.groupedDocuments()) {
        this.isLoading.set(false);
      }
    });
  }

  openDocument(document: ConsultantDocument, isUploadDoc = false) {
    this.documentOpen.emit({
      document: document,
      isUploadDoc: isUploadDoc,
    });
  }

  groupByTypeDocument(documents: ConsultantDocument[] | null): GroupedDocuments[] | null {
    if (documents == null) return documents;

    const grouped = documents.reduce(
      (acc, current) => {
        const docType = current.typeDocument as DocumentCanBeUploadedBoType;
        const defaultDocSection = DOCUMENT_CAN_BE_UPLOADED_BO_SECTION[docType];
        const key = current?.section
          ? DOCUMENT_SECTION_LABELS[current.section]
          : defaultDocSection
            ? DOCUMENT_SECTION_LABELS[defaultDocSection]
            : 'Autre';
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(current);
        return acc;
      },
      {} as Record<string, ConsultantDocument[]>
    );

    // map document by section and put the "Autre" section in the end
    return Object.keys(grouped)
      .map(key => ({
        sectionDocument: key,
        documents: grouped[key],
      }))
      .sort((a, b) => (a.sectionDocument > b.sectionDocument ? -1 : 1))
      .sort(a => (a.sectionDocument === 'Autre' ? 1 : -1));
  }

  onAddDocumentClick() {
    this.fileInput?.nativeElement.click();
  }

  onFileInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (!files || files.length === 0) {
      this.clearSelectedFile();
      return;
    }

    this.handleSelectedFiles(files);
  }

  onDocumentDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDocumentDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;

    if (!files || files.length === 0) {
      return;
    }

    this.assignFilesToInput(files);
    const inputFiles = this.fileInput?.nativeElement.files;

    this.handleSelectedFiles(inputFiles ?? files);
  }

  clearSelectedFile() {
    this.selectedFile.set(undefined);
    this.fileUrls.set([]);
    this.filenames.set([]);
    this.revokeCurrentObjectUrl();

    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private assignFilesToInput(files: FileList) {
    const inputRef = this.fileInput?.nativeElement;
    if (!inputRef) {
      return;
    }

    if (typeof DataTransfer === 'undefined') {
      return;
    }

    const dataTransfer = new DataTransfer();
    Array.from(files).forEach(file => dataTransfer.items.add(file));
    inputRef.files = dataTransfer.files;
  }

  private handleSelectedFiles(files: FileList) {
    const file = files.item(0);

    if (!file) {
      return;
    }

    this.revokeCurrentObjectUrl();

    this.currentObjectUrl = URL.createObjectURL(file);
    this.selectedFile.set(file);
    this.fileUrls.set([this.currentObjectUrl]);
    this.filenames.set([file.name]);

    const newDoc = {
      date: new Date(),
      documentName: 'Nouveau document',
      fileName: file.name,
      id: '',
      typeDocument: 'photo_identite',
      url: this.currentObjectUrl,
      file: file,
    } as ConsultantDocument;

    this.openDocument(newDoc, true);
  }

  private revokeCurrentObjectUrl() {
    if (!this.currentObjectUrl) {
      return;
    }

    URL.revokeObjectURL(this.currentObjectUrl);
    this.currentObjectUrl = undefined;
  }

  private openUpdateDocSectionDialog(
    documentSection: string,
    documentName: string,
    documentId: string
  ) {
    const dialogRef = this.dialogService.openDialog(UpdateDocSectionDialogComponent, {
      documentSection: REVERSED_DOCUMENT_SECTION_LABELS[documentSection],
      documentName,
      documentId,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result) {
          this.onRefreshDocs.emit();
        }
      });
  }

  private openDocRenouvellementManuelDialog(documentName: string, documentId: string) {
    const dialogRef = this.dialogService.openDialog(
      DocumentRenouvellementManuelDialogComponent,
      {
        documentName,
        documentId,
      }
    );

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result) {
          this.onRefreshDocs.emit();
        }
      });
  }
}
